import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  Avatar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  CalendarToday,
  Menu as MenuIcon,
  Close,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';

const DocumentationPage: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [signupsEnabled, setSignupsEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    const checkSignupStatus = async () => {
      try {
        const response = await api.get('/auth/signup-status');
        setSignupsEnabled(response.data.signupsEnabled);
      } catch {
        setSignupsEnabled(true);
      }
    };
    checkSignupStatus();
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="sticky" sx={{
        bgcolor: 'white',
        color: 'text.primary',
        boxShadow: 1,
        zIndex: (theme) => theme.zIndex.drawer + 1
      }}>
        <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
          {/* Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Avatar sx={{
              width: 40,
              height: 40,
              bgcolor: '#10b981'
            }}>
              <CalendarToday />
            </Avatar>
            <Typography variant="h6" sx={{ color: 'text.primary' }}>
              Player Central
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3, ml: 4, flexGrow: 1 }}>
            <Button
              color="inherit"
              onClick={() => navigate('/')}
            >
              Home
            </Button>
            <Button
              color="inherit"
              onClick={() => navigate('/pricing')}
            >
              Pricing
            </Button>
            <Button
              color="inherit"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              sx={{
                borderBottom: '2px solid #10b981',
                borderRadius: 0,
              }}
            >
              Documentation
            </Button>
          </Box>

          {/* Auth Buttons - Desktop */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              sx={{
                color: 'text.primary',
                borderColor: 'text.primary',
                '&:hover': {
                  borderColor: 'success.main',
                  color: 'success.main'
                }
              }}
              onClick={() => navigate('/login')}
            >
              Login
            </Button>
            {signupsEnabled && (
              <Button
                variant="contained"
                sx={{
                  bgcolor: 'success.main',
                  '&:hover': { bgcolor: 'success.dark' }
                }}
                onClick={() => navigate('/register')}
              >
                Sign Up
              </Button>
            )}
          </Box>

          {/* Mobile Menu Button */}
          <Box sx={{ flexGrow: 1, display: { xs: 'block', md: 'none' } }} />
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="end"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            sx={{ display: { xs: 'block', md: 'none' } }}
          >
            {mobileMenuOpen ? <Close /> : <MenuIcon />}
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        variant="temporary"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        anchor="right"
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <Toolbar />
          </Box>

          <Box>
            <List>
              <ListItem
                button
                onClick={() => {
                  navigate('/');
                  setMobileMenuOpen(false);
                }}
              >
                <ListItemText primary="Home" />
              </ListItem>
              <ListItem
                button
                onClick={() => {
                  navigate('/pricing');
                  setMobileMenuOpen(false);
                }}
              >
                <ListItemText primary="Pricing" />
              </ListItem>
              <ListItem
                button
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
              >
                <ListItemText primary="Documentation" />
              </ListItem>
              <ListItem>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    color: 'text.primary',
                    borderColor: 'text.primary',
                    mb: 1
                  }}
                  onClick={() => navigate('/login')}
                >
                  Login
                </Button>
              </ListItem>
              {signupsEnabled && (
                <ListItem>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ bgcolor: 'success.main' }}
                    onClick={() => navigate('/register')}
                  >
                    Sign Up
                  </Button>
                </ListItem>
              )}
            </List>
          </Box>
        </Box>
      </Drawer>

      {/* Hero Section with green background */}
      <Box sx={{
        background: 'linear-gradient(to bottom, rgba(16,185,129,0.04) 0%, white 100%)',
        py: { xs: 6, md: 8 }
      }}>
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
              Documentation
            </Typography>
            <Typography variant="h5" color="text.secondary" mb={4}>
              Coming Soon
            </Typography>
            <Typography variant="body1" color="text.secondary" maxWidth="600px" mx="auto">
              We're working hard to create comprehensive documentation to help you get the most out of Player Central.
              Check back soon for detailed guides, tutorials, and API documentation.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default DocumentationPage;
